import React, { useState, useEffect } from 'react';
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { ArrowLeft } from 'lucide-react';
import MabelHeader from './MabelHeader';
import TasksView from './TasksView';
import MoneyView from './Money/MoneyView';
import { WidgetGrid } from '../Layout';
import { WIDGET_COMPONENTS } from '../Widgets';
import { getDefaultLayout } from '../../config/domains';
import { useToast } from '../Toast';
import { checkStaleness } from '../../utils/stateApi';
import { getPreferences } from '../../utils/memoryApi';
import { ABLE_COLORS } from '../../constants';

/**
 * Hub - The Life Console container
 *
 * Manages internal views:
 * - 'home': Dashboard with navigation cards
 * - 'tasks': Focus/Plan modes for task management
 * - 'kitchen': Kitchen inventory and meal planning
 *
 * Uses 900px max-width for comfortable desktop viewing.
 */
export default function Hub({
  currentDomain = 'hub',
  onDomainChange,
  user,
  tasks,
  filteredTasks,
  currentTask,
  focusIndex,
  currentState,
  onStateChange,
  onNavigate,
  onOpenChat,
  onOpenChatWithMessage,
  onOpenActivityLog,
  onClearActivityLog,
  onAddTask,
  onMarkDone,
  onMarkUndone,
  onSkip,
  onUpdateTask,
  onDeleteTask,
  onToggleStar,
  onClearAllTasks,
  onLogStateCheckin,
  // Review counts
  pendingFactsCount = 0,
  pendingAliasesCount = 0,
  onOpenReviewModal,
  onReviewChanged,
  // Activity log
  activityLogEntries = [],
  // Filter props
  capacity,
  setCapacity,
  statusFilter,
  setStatusFilter,
  showStarredOnly,
  setShowStarredOnly,
  expandedFilter,
  setExpandedFilter,
  contextOptions,
  // Kitchen inventory props
  stockedItems = [],
  shoppingList = [],
  boughtItems = [],
  inventoryCategories = [],
  onAddInventoryItem,
  onAddInventoryItems,
  onUpdateInventoryItem,
  onRemoveInventoryItem,
  onMarkAsOut,
  onAddToShoppingList,
  onMarkAsBought,
  onUndoBought,
  // Meal plan props
  mealPlanMeals = [],
  mealPlanWeekStart,
  mealPlanLoading = false,
  mealPlanSaving = false,
  onAddMeal,
  onRemoveMeal,
  onSkipMealDay,
  onClearMealDay,
  onSetMealPlanWeekStart,
  // Food log props
  foodLogDate,
  foodLogEntries = [],
  entriesByMeal = {},
  foodLogTotals = {},
  foodLogLoading = false,
  foodLogSaving = false,
  foodLogIsToday = true,
  trackedMacros = ['protein', 'carbs', 'fat'],
  dailyTargets = {},
  availableMacros = [],
  onAddFoodEntry,
  onDeleteFoodEntry,
  onChangeFoodLogDate,
  onGoToFoodLogToday,
  onAddTrackedMacro,
  onRemoveTrackedMacro,
  // Layout props
  isMobile = false,
  onOpenSidebar,
  widgetLayout = null
}) {
  const { showToast } = useToast();
  const { getToken, isAuthenticated } = useKindeAuth();

  // Map currentDomain to internal view
  // 'hub' domain = 'home' view, others map directly
  const hubView = currentDomain === 'hub' ? 'home' : currentDomain;
  const setHubView = (view) => {
    // Map 'home' back to 'hub' domain
    const domain = view === 'home' ? 'hub' : view;
    if (onDomainChange) {
      onDomainChange(domain);
    }
  };

  // Staleness detection
  // eslint-disable-next-line no-unused-vars
  const [stalenessInfo, setStalenessInfo] = useState(null);

  // Kitchen sub-view navigation
  // null = dashboard, 'pantry' | 'shopping' | 'meals' | 'mealplan' | 'foodlog' = sub-view
  const [kitchenSubView, setKitchenSubView] = useState(null);

  // Reset kitchen sub-view when leaving kitchen domain
  useEffect(() => {
    if (currentDomain !== 'kitchen') {
      setKitchenSubView(null);
    }
  }, [currentDomain]);

  // Display name and date format from preferences
  const [displayName, setDisplayName] = useState(null);
  const [dateFormat, setDateFormat] = useState('DMY');

  // Load preferences on mount
  useEffect(() => {
    async function loadPreferences() {
      if (!isAuthenticated) return;
      try {
        const token = await getToken();
        if (token) {
          const result = await getPreferences(token);
          if (result.success) {
            if (result.preferences.display_name) {
              setDisplayName(result.preferences.display_name);
            }
            if (result.preferences.date_format) {
              setDateFormat(result.preferences.date_format);
            }
          }
        }
      } catch (err) {
        console.warn('Failed to load preferences:', err);
      }
    }
    loadPreferences();
  }, [isAuthenticated, getToken]);

  // Generate staleness prompt based on time of day
  const getLocalStalenessPrompt = React.useCallback(() => {
    const hour = new Date().getHours();
    if (hour < 12) {
      return "Good morning! How are you doing?";
    } else if (hour < 17) {
      return "It's been a while - how are you now?";
    } else {
      return "Evening check-in - how are you feeling?";
    }
  }, []);

  // Check staleness on mount and when state changes
  useEffect(() => {
    async function checkStateStatus() {
      if (!isAuthenticated) return;

      try {
        const token = await getToken();
        if (token) {
          const result = await checkStaleness(token);
          setStalenessInfo(result);
        }
      } catch (err) {
        // Fall back to local staleness check
        if (currentState.timestamp) {
          const hoursSince = (Date.now() - new Date(currentState.timestamp).getTime()) / (1000 * 60 * 60);
          if (hoursSince >= 6) {
            setStalenessInfo({
              isStale: true,
              hoursSinceUpdate: Math.round(hoursSince * 10) / 10,
              prompt: getLocalStalenessPrompt()
            });
          } else {
            setStalenessInfo({ isStale: false });
          }
        }
      }
    }

    checkStateStatus();
  }, [isAuthenticated, getToken, currentState.timestamp, getLocalStalenessPrompt]);

  // Clear staleness when state is updated and log to activity log
  const handleStateChange = (newState) => {
    onStateChange(newState);
    setStalenessInfo({ isStale: false });
    // Log the check-in to activity log
    if (onLogStateCheckin) {
      onLogStateCheckin(newState);
    }
  };

  // Calculate counts
  const activeTasks = tasks.filter(t => !t.done);
  
  // Count completed tasks from activity log (for welcome back message)
  const recentCompletedCount = activityLogEntries.filter(
    e => e.type === 'task_completed'
  ).length;


  return (
    <div 
      className="hub-container"
      style={{
        maxWidth: '900px',
        width: '100%',
        margin: '0 auto',
        boxSizing: 'border-box',
        overflowX: 'hidden'
      }}
    >
      <style>{`
        .hub-container {
          padding: 20px;
        }
        @media (max-width: 500px) {
          .hub-container {
            padding: 16px 12px;
          }
        }
      `}</style>
      {/* Kitchen View - Dashboard or Sub-view */}
      {hubView === 'kitchen' && (
        <>
          {/* Kitchen Header */}
          <MabelHeader 
            user={user} 
            currentState={currentState}
            onStateChange={handleStateChange}
            displayName={displayName}
            dateFormat={dateFormat}
            onDisplayNameChange={setDisplayName}
            onDateFormatChange={setDateFormat}
            recentCompletedCount={recentCompletedCount}
            isMobile={isMobile}
            onOpenSidebar={onOpenSidebar}
            onOpenChat={onOpenChat}
            showToast={showToast}
          />

          {/* Sub-view back button */}
          {kitchenSubView && (
            <button
              onClick={() => setKitchenSubView(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 0',
                marginBottom: '12px',
                background: 'none',
                border: 'none',
                color: ABLE_COLORS.primary,
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              <ArrowLeft size={18} />
              Back to Kitchen
            </button>
          )}

          {/* Kitchen Dashboard - Card Grid */}
          {!kitchenSubView && (
            <WidgetGrid
              widgetIds={widgetLayout?.length ? widgetLayout : getDefaultLayout('kitchen')}
              domain="kitchen"
              widgetComponents={WIDGET_COMPONENTS}
              widgetProps={{
                tonightCard: {
                  meals: mealPlanMeals,
                  weekStart: mealPlanWeekStart,
                  onNavigate: setKitchenSubView
                },
                expiringCard: {
                  stockedItems,
                  onNavigate: setKitchenSubView
                },
                shoppingCard: {
                  shoppingList,
                  onNavigate: setKitchenSubView
                },
                pantryCard: {
                  stockedItems,
                  onNavigate: setKitchenSubView
                },
                mealsCard: {
                  stockedItems,
                  onNavigate: setKitchenSubView
                },
                foodLogCard: {
                  entries: foodLogEntries,
                  isToday: foodLogIsToday,
                  onNavigate: setKitchenSubView
                },
                askMabel: {
                  onOpenChat
                }
              }}
            />
          )}

          {/* Kitchen Sub-views - Full Widgets */}
          {kitchenSubView === 'pantry' && (
            <div style={{ marginTop: '8px' }}>
              {React.createElement(WIDGET_COMPONENTS.pantry, {
                stockedItems,
                shoppingList,
                categories: inventoryCategories,
                onAddItem: onAddInventoryItem,
                onAddItems: onAddInventoryItems,
                onUpdateItem: onUpdateInventoryItem,
                onRemoveItem: onRemoveInventoryItem,
                onMarkAsOut,
                onAddToShoppingList,
                showToast
              })}
            </div>
          )}

          {kitchenSubView === 'shopping' && (
            <div style={{ marginTop: '8px' }}>
              {React.createElement(WIDGET_COMPONENTS.shopping, {
                shoppingList,
                boughtItems,
                categories: inventoryCategories,
                onAddItem: onAddInventoryItem,
                onUpdateItem: onUpdateInventoryItem,
                onRemoveItem: onRemoveInventoryItem,
                onMarkAsBought,
                onUndoBought,
                showToast
              })}
            </div>
          )}

          {kitchenSubView === 'meals' && (
            <div style={{ marginTop: '8px' }}>
              {React.createElement(WIDGET_COMPONENTS.meals, {
                stockedItems,
                currentState,
                onOpenChatWithMessage
              })}
            </div>
          )}

          {kitchenSubView === 'mealplan' && (
            <div style={{ marginTop: '8px' }}>
              {React.createElement(WIDGET_COMPONENTS.mealPlan, {
                stockedItems,
                currentState,
                meals: mealPlanMeals,
                weekStart: mealPlanWeekStart,
                isLoading: mealPlanLoading,
                isSaving: mealPlanSaving,
                onAddMeal,
                onRemoveMeal,
                onSkipDay: onSkipMealDay,
                onClearDay: onClearMealDay,
                onWeekChange: onSetMealPlanWeekStart,
                onAddItem: onAddInventoryItem,
                showToast
              })}
            </div>
          )}

          {kitchenSubView === 'foodlog' && (
            <div style={{ marginTop: '8px' }}>
              {React.createElement(WIDGET_COMPONENTS.foodLog, {
                selectedDate: foodLogDate,
                entries: foodLogEntries,
                entriesByMeal,
                totals: foodLogTotals,
                isLoading: foodLogLoading,
                isSaving: foodLogSaving,
                isToday: foodLogIsToday,
                trackedMacros,
                dailyTargets,
                availableMacros,
                onAddEntry: onAddFoodEntry,
                onDeleteEntry: onDeleteFoodEntry,
                onDateChange: onChangeFoodLogDate,
                onGoToToday: onGoToFoodLogToday,
                onAddTrackedMacro,
                onRemoveTrackedMacro,
                showToast
              })}
            </div>
          )}
        </>
      )}

      {/* Money View */}
      {hubView === 'money' && (
        <MoneyView onBack={() => setHubView('home')} />
      )}

      {/* Tasks View */}
      {hubView === 'tasks' && (
        <TasksView
          tasks={tasks}
          filteredTasks={filteredTasks || activeTasks}
          currentTask={currentTask}
          focusIndex={focusIndex || 0}
          onBack={() => setHubView('home')}
          onOpenChat={onOpenChat}
          onAddTask={onAddTask}
          onMarkDone={onMarkDone}
          onMarkUndone={onMarkUndone}
          onSkip={onSkip}
          onUpdateTask={onUpdateTask}
          onDeleteTask={onDeleteTask}
          onToggleStar={onToggleStar}
          onClearAllTasks={onClearAllTasks}
          // State check-in props
          currentState={currentState}
          onStateChange={handleStateChange}
          // Filter props
          capacity={capacity}
          setCapacity={setCapacity}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          showStarredOnly={showStarredOnly}
          setShowStarredOnly={setShowStarredOnly}
          expandedFilter={expandedFilter}
          setExpandedFilter={setExpandedFilter}
          contextOptions={contextOptions}
        />
      )}

      {/* Home View */}
      {hubView === 'home' && (
        <>
          {/* Mabel Header */}
          <MabelHeader 
            user={user} 
            currentState={currentState}
            onStateChange={handleStateChange}
            displayName={displayName}
            dateFormat={dateFormat}
            onDisplayNameChange={setDisplayName}
            onDateFormatChange={setDateFormat}
            recentCompletedCount={recentCompletedCount}
            isMobile={isMobile}
            onOpenSidebar={onOpenSidebar}
            onOpenChat={onOpenChat}
            showToast={showToast}
          />

          {/* Widget Grid */}
          <WidgetGrid
            widgetIds={widgetLayout?.length ? widgetLayout : getDefaultLayout('hub')}
            domain="hub"
            widgetComponents={WIDGET_COMPONENTS}
            widgetProps={{
              stateCards: {
                currentState,
                onStateChange: handleStateChange,
                onToast: showToast
              },
              todaysFocus: {
                tasks,
                onNavigate: () => setHubView('tasks')
              },
              askMabel: {
                onOpenChat
              },
              tasksCard: {
                tasks,
                onNavigate: setHubView
              },
              kitchenCard: {
                onNavigate: setHubView
              },
              moneyCard: {
                onNavigate: setHubView
              },
              roadmapCard: {
                tasks,
                onNavigate
              },
              recentWins: {
                entries: activityLogEntries,
                onOpenFull: onOpenActivityLog,
                onClearWins: onClearActivityLog
              },
              review: {
                pendingFactsCount,
                pendingAliasesCount,
                onOpenReviewModal,
                onReviewChanged
              },
              install: {}
            }}
          />
        </>
      )}
    </div>
  );
}
